/**
 * PURPOSE: Pestaña "Categorías" de un deporte individual: inscritos de cada categoría (prueba) del evento.
 * RESPONSIBILITIES: Listar las categorías con sus inscritos vigentes; agregar un deportista acreditado (búsqueda por
 *   nombre, documento o universidad), retirarlo de la prueba y moverlo a otra prueba (FTR-0059). Todo lo valida el
 *   servidor (rol, compañía, categoría oficializada, medallas, elegibilidad); aquí solo se muestran sus mensajes.
 *   Una categoría ligada a una prueba de atletismo (prop `athletics`) agrega con la MISMA ventana que Resultados: inscribe
 *   si falta y lo pone en la lista de salida (serie y carril en pista; inscripciones cerradas no lo impiden mientras la
 *   serie no sea oficial). Las demás categorías conservan el agregado solo-inscripción. Cuando no se puede agregar, la
 *   pantalla dice por qué; nunca queda un campo bloqueado sin explicación.
 *   En atletismo, mover o retirar no saca al atleta de una serie ya armada: se avisa para hacerlo en "Resultados".
 * COLLABORATORS: services/individualEntriesService.js, sections/CategoriaSection.jsx, componentes compartidos de
 *   AtletismoResultadosArea (ui.jsx, ConfirmDialog.jsx, AddAthleteModal.jsx).
 * ROUTE: winscore/src/area/CategoriasIndividualesArea/CategoriasIndividualesArea.jsx
 */
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import { toast } from "react-hot-toast";
import { FiArrowRight, FiSearch, FiTrash2, FiUserPlus, FiUsers } from "react-icons/fi";

import {
  INELIGIBLE_REASON_LABELS,
  addIndividualEntry,
  getIndividualEntriesStatus,
  individualEntriesErrorMessage,
  listIndividualCategories,
  moveIndividualEntry,
  removeIndividualEntry,
  searchIndividualCandidates,
} from "../../services/individualEntriesService.js";
import AddAthleteModal from "../AtletismoResultadosArea/components/AddAthleteModal.jsx";
import { useConfirmDialog } from "../AtletismoResultadosArea/components/ConfirmDialog.jsx";
import { isRoundLocked, roundName } from "../AtletismoResultadosArea/placement.js";
import {
  Badge,
  Card,
  EmptyState,
  ErrorState,
  LoadingState,
  Spinner,
  compactSelectClass,
  secondaryButtonClass,
} from "../AtletismoResultadosArea/components/ui.jsx";

const STATUS_REASON_TEXT = Object.freeze({
  COMPANY_NOT_ENABLED: "La gestión de categorías no está habilitada para la compañía de este evento.",
  SPORT_NOT_INDIVIDUAL: "Este deporte no es individual.",
  SCHEMA_NOT_READY: "Falta aplicar en la base de datos el SQL de resultados por categoría.",
  FEATURE_DISABLED: "La gestión de categorías está apagada en el servidor.",
});

const MIN_SEARCH = 2;

const normalize = (value) =>
  String(value || "")
    .normalize("NFD")
    .replace(/[̀-ͯ]/g, "")
    .toLowerCase();

const activeEntriesOf = (category) =>
  (Array.isArray(category?.entries) ? category.entries : []).filter((entry) => entry.entryActive);

const institutionText = (institution) => institution?.abrev || institution?.name || "—";

const CategoryList = ({ categories, selectedId, onSelect, filter, onFilter }) => {
  const visible = categories.filter((category) => !filter || normalize(category.label).includes(normalize(filter)));
  return (
    <Card className="flex max-h-[75vh] flex-col overflow-hidden">
      <div className="border-b border-slate-100 p-3">
        <label className="flex items-center gap-2 rounded-lg border border-slate-300 px-2 py-1.5 focus-within:border-indigo-400">
          <FiSearch className="shrink-0 text-slate-400" aria-hidden="true" />
          <input
            type="search"
            value={filter}
            onChange={(event) => onFilter(event.target.value)}
            placeholder="Buscar categoría"
            aria-label="Buscar categoría"
            className="min-w-0 flex-1 bg-transparent text-sm outline-none"
          />
        </label>
      </div>
      <ul className="min-h-0 flex-1 overflow-y-auto p-2">
        {visible.map((category) => {
          const active = category.idcategory === selectedId;
          const count = activeEntriesOf(category).length;
          return (
            <li key={category.idcategory}>
              <button
                type="button"
                onClick={() => onSelect(category.idcategory)}
                title={category.label}
                aria-current={active ? "true" : undefined}
                className={`flex w-full items-center justify-between gap-2 rounded-lg px-3 py-2 text-left text-sm transition ${
                  active ? "bg-indigo-600 text-white" : "text-slate-700 hover:bg-slate-50"
                } ${category.enabledInEvent ? "" : "opacity-60"}`}
              >
                <span className="min-w-0 flex-1 truncate font-semibold">{category.label}</span>
                <span className={`shrink-0 text-xs font-black ${active ? "text-indigo-100" : "text-slate-400"}`}>{count}</span>
              </button>
            </li>
          );
        })}
        {!visible.length ? <li className="px-3 py-2 text-xs italic text-slate-400">Sin coincidencias.</li> : null}
      </ul>
    </Card>
  );
};

const AddAthletePanel = ({ scope, category, disabled, onAdded }) => {
  const [query, setQuery] = useState("");
  const [results, setResults] = useState(null);
  const [searching, setSearching] = useState(false);
  const [busyId, setBusyId] = useState(null);

  useEffect(() => {
    const text = query.trim();
    if (text.length < MIN_SEARCH) {
      setResults(null);
      return undefined;
    }
    let cancelled = false;
    const timer = setTimeout(async () => {
      setSearching(true);
      try {
        const candidates = await searchIndividualCandidates({ ...scope, idcategory: category.idcategory, query: text });
        if (!cancelled) setResults(candidates);
      } catch (error) {
        if (!cancelled) {
          setResults([]);
          toast.error(individualEntriesErrorMessage(error, "No se pudo buscar."));
        }
      } finally {
        if (!cancelled) setSearching(false);
      }
    }, 300);
    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [query, scope, category.idcategory]);

  const add = async (candidate) => {
    setBusyId(candidate.idacreditation);
    try {
      await addIndividualEntry({ ...scope, idcategory: category.idcategory, idacreditation: candidate.idacreditation });
      toast.success(`${candidate.name} agregado a ${category.label}.`);
      setResults((current) =>
        Array.isArray(current)
          ? current.map((item) => (item.idacreditation === candidate.idacreditation ? { ...item, alreadyInCategory: true } : item))
          : current
      );
      onAdded();
    } catch (error) {
      toast.error(individualEntriesErrorMessage(error, "No se pudo agregar."));
    } finally {
      setBusyId(null);
    }
  };

  return (
    <div className="border-b border-slate-100 p-4">
      <p className="mb-2 flex items-center gap-1.5 text-xs font-black uppercase tracking-wide text-slate-500">
        <FiUserPlus aria-hidden="true" /> Agregar deportista
      </p>
      <label className="flex items-center gap-2 rounded-lg border border-slate-300 px-2 py-1.5 focus-within:border-indigo-400">
        <FiSearch className="shrink-0 text-slate-400" aria-hidden="true" />
        <input
          type="search"
          value={query}
          onChange={(event) => setQuery(event.target.value)}
          disabled={disabled}
          placeholder="Nombre, documento o universidad (mín. 2 letras)"
          aria-label="Buscar deportista para agregar"
          className="min-w-0 flex-1 bg-transparent text-sm outline-none disabled:text-slate-400"
        />
        {searching ? <Spinner /> : null}
      </label>
      {Array.isArray(results) ? (
        results.length ? (
          <ul className="mt-2 max-h-64 space-y-1 overflow-y-auto">
            {results.map((candidate) => {
              const reason = candidate.eligible ? null : INELIGIBLE_REASON_LABELS[candidate.ineligibleReason] || "No elegible";
              return (
                <li
                  key={candidate.idacreditation}
                  className="flex items-center justify-between gap-2 rounded-lg border border-slate-200 bg-white px-3 py-1.5"
                >
                  <div className="min-w-0">
                    <p className="truncate text-sm font-semibold text-slate-800" title={candidate.name}>
                      {candidate.name}
                    </p>
                    <p className="text-[11px] font-bold text-slate-500">
                      {institutionText(candidate.institution)}
                      {candidate.document ? ` · ${candidate.document}` : ""}
                      {reason ? <span className="ml-1 text-rose-600">· {reason}</span> : null}
                    </p>
                  </div>
                  {candidate.alreadyInCategory ? (
                    <Badge tone="green">Inscrito</Badge>
                  ) : (
                    <button
                      type="button"
                      onClick={() => add(candidate)}
                      disabled={disabled || !candidate.eligible || busyId !== null}
                      className={secondaryButtonClass}
                    >
                      {busyId === candidate.idacreditation ? <Spinner /> : <FiUserPlus aria-hidden="true" />} Agregar
                    </button>
                  )}
                </li>
              );
            })}
          </ul>
        ) : (
          <p className="mt-2 text-xs italic text-slate-500">Ningún deportista acreditado coincide.</p>
        )
      ) : null}
    </div>
  );
};

/** Prueba de atletismo (individual o con sub-pruebas sueltas) a la que pertenece la categoría, o null. */
export const pruebaOfCategory = (athletics, idcategory) =>
  (Array.isArray(athletics?.pruebas) ? athletics.pruebas : []).find(
    (prueba) => prueba.format !== "PST" && prueba.format !== "PCB" && Array.isArray(prueba.categoryIds) && prueba.categoryIds.includes(idcategory)
  ) || null;

/** Agregar a la prueba (inscripción + lista de salida): abre la misma ventana que "Resultados". */
const PruebaAddPanel = ({ athletics, prueba, disabled, onAdded }) => {
  const [loading, setLoading] = useState(false);
  const [detail, setDetail] = useState(null);
  const official = prueba.status === "official";
  const pruebaId = prueba.id;
  const searchCandidates = athletics.searchCandidates;
  // Estable entre renders: la ventana de búsqueda depende de esta función.
  const search = useCallback((query) => searchCandidates({ pruebaId, query }), [pruebaId, searchCandidates]);

  const open = async () => {
    setLoading(true);
    try {
      setDetail(await athletics.loadPrueba(prueba.id));
    } catch (error) {
      toast.error(individualEntriesErrorMessage(error, "No se pudo cargar la prueba."));
    } finally {
      setLoading(false);
    }
  };

  const submit = async ({ idacreditation, roundId, lane, candidate, round }) => {
    try {
      await athletics.addAthlete({ pruebaId: detail.prueba.id, idacreditation, roundId, lane, expectedRevision: detail.prueba.revision });
      toast.success(`${candidate.name} agregado a ${roundName(round)}${lane ? ` (carril ${lane})` : ""}.`);
      onAdded();
      return { ok: true };
    } catch (error) {
      return { ok: false, message: individualEntriesErrorMessage(error, "No se pudo agregar.") };
    }
  };

  const rounds = detail?.rounds || [];
  const firstOpen = rounds.find((round) => !isRoundLocked(round)) || rounds[0];
  return (
    <div className="border-b border-slate-100 p-4">
      <p className="mb-2 flex items-center gap-1.5 text-xs font-black uppercase tracking-wide text-slate-500">
        <FiUserPlus aria-hidden="true" /> Agregar deportista
      </p>
      {official ? (
        <p className="text-xs font-semibold text-slate-600">
          La prueba "{prueba.label}" está oficializada: reábrela en Resultados para agregar deportistas.
        </p>
      ) : (
        <>
          <p className="mb-2 text-xs text-slate-500">
            Se inscribe en esta categoría (si falta) y queda en la lista de salida de "{prueba.label}"; en pista eliges serie y
            carril. Sirve aunque las inscripciones estén cerradas, mientras la serie no esté terminada.
          </p>
          <button type="button" onClick={open} disabled={disabled || loading} className={secondaryButtonClass}>
            {loading ? <Spinner /> : <FiUserPlus aria-hidden="true" />} Agregar deportista a la prueba
          </button>
        </>
      )}
      {detail ? (
        <AddAthleteModal
          open
          prueba={detail.prueba}
          rounds={rounds}
          defaultRoundId={firstOpen ? firstOpen.id : null}
          searchCandidates={search}
          onSubmit={submit}
          onClose={() => setDetail(null)}
        />
      ) : null}
    </div>
  );
};

const EntriesTable = ({ category, moveTargets, disabled, busyKey, onMove, onRemove }) => {
  const [targets, setTargets] = useState({});
  const entries = activeEntriesOf(category);
  if (!entries.length) {
    return <p className="px-4 py-4 text-sm italic text-slate-500">Esta categoría no tiene inscritos vigentes.</p>;
  }
  return (
    <div className="overflow-x-auto">
      <table className="w-full min-w-[640px] table-auto">
        <thead>
          <tr className="border-b border-slate-200 bg-slate-50 text-[11px] font-bold uppercase tracking-wide text-slate-500">
            <th className="px-3 py-2 text-center">#</th>
            <th className="px-3 py-2 text-left">Deportista</th>
            <th className="px-3 py-2 text-left">Univ.</th>
            <th className="px-3 py-2 text-left">Mover a otra prueba</th>
            <th className="px-3 py-2 text-center" aria-label="Retirar" />
          </tr>
        </thead>
        <tbody>
          {entries.map((entry, index) => {
            const target = targets[entry.idacreditation] ?? "";
            const busy = busyKey === entry.idacreditation;
            return (
              <tr key={entry.idacreditation} className="border-b border-slate-100 last:border-b-0">
                <td className="px-3 py-2 text-center text-xs font-bold text-slate-400">{index + 1}</td>
                <td className="px-3 py-2 text-sm font-semibold text-slate-800">
                  {entry.name}
                  {entry.medal ? <Badge tone="amber" className="ml-1">Medalla</Badge> : null}
                </td>
                <td className="px-3 py-2 text-xs font-bold text-slate-600" title={entry.institution?.name || undefined}>
                  {institutionText(entry.institution)}
                </td>
                <td className="px-3 py-2">
                  <div className="flex items-center gap-1.5">
                    <select
                      value={target}
                      onChange={(event) => setTargets((current) => ({ ...current, [entry.idacreditation]: event.target.value }))}
                      disabled={disabled || busy}
                      aria-label={`Prueba de destino de ${entry.name}`}
                      className={`${compactSelectClass} max-w-[16rem]`}
                    >
                      <option value="">Elegir prueba…</option>
                      {moveTargets.map((option) => (
                        <option key={option.idcategory} value={option.idcategory}>
                          {option.label}
                        </option>
                      ))}
                    </select>
                    <button
                      type="button"
                      onClick={() => onMove(entry, Number(target))}
                      disabled={disabled || busy || !target}
                      className={secondaryButtonClass}
                      aria-label={`Mover a ${entry.name}`}
                    >
                      {busy ? <Spinner /> : <FiArrowRight aria-hidden="true" />} Mover
                    </button>
                  </div>
                </td>
                <td className="px-3 py-2 text-center">
                  <button
                    type="button"
                    onClick={() => onRemove(entry)}
                    disabled={disabled || busy}
                    className="inline-flex items-center gap-1 rounded-md px-2 py-1 text-xs font-bold text-rose-600 hover:bg-rose-50 disabled:opacity-40"
                    aria-label={`Retirar a ${entry.name}`}
                    title="Retirar de esta prueba"
                  >
                    <FiTrash2 aria-hidden="true" /> Retirar
                  </button>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
};

const CategoriasIndividualesArea = ({ athletics = null }) => {
  const { idevent, idsport } = useParams();
  const scope = useMemo(() => ({ idevent, idsport }), [idevent, idsport]);
  const { confirm, dialog } = useConfirmDialog();
  const [status, setStatus] = useState(null);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedId, setSelectedId] = useState(null);
  const [filter, setFilter] = useState("");
  const [busyKey, setBusyKey] = useState(null);

  const load = useCallback(
    async ({ quiet = false } = {}) => {
      if (!quiet) setLoading(true);
      setError(null);
      try {
        const nextStatus = await getIndividualEntriesStatus(scope);
        setStatus(nextStatus);
        if (!nextStatus.enabled) {
          setCategories([]);
          return;
        }
        const list = await listIndividualCategories(scope);
        setCategories(list);
        setSelectedId((current) => (list.some((item) => item.idcategory === current) ? current : list[0]?.idcategory ?? null));
      } catch (loadError) {
        setError(individualEntriesErrorMessage(loadError, "No se pudieron cargar las categorías."));
      } finally {
        setLoading(false);
      }
    },
    [scope]
  );

  useEffect(() => {
    load();
  }, [load]);

  const selected = categories.find((category) => category.idcategory === selectedId) || null;
  const official = selected?.podium?.status === "OFFICIAL";
  const linkedPrueba = selected ? pruebaOfCategory(athletics, selected.idcategory) : null;
  const canManage = Boolean(status?.canManageEntries) && Boolean(selected?.supported) && !official;
  const canAddToPrueba = Boolean(linkedPrueba) && athletics?.canWrite === true && !official;
  const moveTargets = useMemo(
    () => categories.filter((category) => category.idcategory !== selectedId && category.enabledInEvent && category.supported),
    [categories, selectedId]
  );

  const runMutation = async (entry, action, successText) => {
    setBusyKey(entry.idacreditation);
    try {
      await action();
      toast.success(successText);
      await load({ quiet: true });
    } catch (mutationError) {
      toast.error(individualEntriesErrorMessage(mutationError));
    } finally {
      setBusyKey(null);
    }
  };

  const handleMove = async (entry, targetCategoryId) => {
    const target = categories.find((category) => category.idcategory === targetCategoryId);
    if (!target) return;
    const ok = await confirm({
      title: "Mover de prueba",
      message: `${entry.name} pasará de "${selected.label}" a "${target.label}".`,
      confirmLabel: "Mover",
    });
    if (!ok) return;
    await runMutation(
      entry,
      () => moveIndividualEntry({ ...scope, idcategory: selected.idcategory, idacreditation: entry.idacreditation, targetCategoryId }),
      `${entry.name} movido a ${target.label}.`
    );
  };

  const handleRemove = async (entry) => {
    const ok = await confirm({
      title: "Retirar de la prueba",
      message: `${entry.name} dejará de estar inscrito en "${selected.label}". Se puede volver a agregar.`,
      confirmLabel: "Retirar",
      tone: "danger",
    });
    if (!ok) return;
    await runMutation(
      entry,
      () => removeIndividualEntry({ ...scope, idcategory: selected.idcategory, idacreditation: entry.idacreditation }),
      `${entry.name} retirado de ${selected.label}.`
    );
  };

  if (loading) return <LoadingState label="Cargando categorías..." />;
  if (error) return <ErrorState message={error} onRetry={() => load()} />;
  if (!status?.enabled) {
    return (
      <EmptyState
        title="Categorías no disponibles"
        description={STATUS_REASON_TEXT[status?.reason] || "La gestión de categorías no está disponible para este deporte."}
      />
    );
  }
  if (!categories.length) {
    return <EmptyState title="Sin categorías" description="Este deporte aún no tiene categorías cargadas en el evento." />;
  }

  return (
    <div className="grid grid-cols-1 gap-4 lg:grid-cols-[minmax(240px,320px)_1fr]">
      <CategoryList
        categories={categories}
        selectedId={selectedId}
        onSelect={setSelectedId}
        filter={filter}
        onFilter={setFilter}
      />
      {selected ? (
        <Card className="overflow-hidden">
          <div className="flex flex-wrap items-center justify-between gap-2 border-b border-slate-100 px-4 py-3">
            <h3 className="flex min-w-0 items-center gap-2 text-sm font-black uppercase tracking-wide text-indigo-700">
              <FiUsers aria-hidden="true" />
              <span className="truncate" title={selected.label}>
                {selected.label}
              </span>
            </h3>
            <div className="flex items-center gap-1.5">
              <Badge tone="slate">{activeEntriesOf(selected).length} inscritos</Badge>
              {official ? <Badge tone="green">Resultado oficial</Badge> : null}
              {!selected.enabledInEvent ? <Badge tone="amber">No habilitada en el evento</Badge> : null}
            </div>
          </div>
          {official ? (
            <p className="border-b border-slate-100 bg-emerald-50 px-4 py-2 text-xs text-emerald-800">
              El resultado de esta categoría está oficializado: para cambiar sus inscritos primero hay que reabrirlo.
            </p>
          ) : null}
          {!selected.supported ? (
            <p className="border-b border-slate-100 bg-amber-50 px-4 py-2 text-xs text-amber-800">
              Esta categoría es por parejas: sus inscritos no se gestionan desde aquí.
            </p>
          ) : null}
          {status.canManageEntries ? null : (
            <p className="border-b border-slate-100 bg-slate-50 px-4 py-2 text-xs text-slate-600">
              Tu rol solo puede ver los inscritos.
            </p>
          )}
          {canAddToPrueba ? (
            <PruebaAddPanel
              key={`add-prueba-${selected.idcategory}`}
              athletics={athletics}
              prueba={linkedPrueba}
              disabled={busyKey !== null}
              onAdded={() => load({ quiet: true })}
            />
          ) : canManage && selected.enabledInEvent ? (
            <AddAthletePanel key={`add-${selected.idcategory}`} scope={scope} category={selected} disabled={busyKey !== null} onAdded={() => load({ quiet: true })} />
          ) : canManage ? (
            <p className="border-b border-slate-100 bg-amber-50 px-4 py-2 text-xs text-amber-800">
              Inscripciones cerradas: esta prueba no está habilitada en el evento y desde aquí no se pueden agregar deportistas.
            </p>
          ) : null}
          <EntriesTable
            key={`entries-${selected.idcategory}`}
            category={selected}
            moveTargets={moveTargets}
            disabled={!canManage || busyKey !== null}
            busyKey={busyKey}
            onMove={handleMove}
            onRemove={handleRemove}
          />
          <p className="border-t border-slate-100 px-4 py-2 text-[11px] text-slate-500">
            En atletismo, mover o retirar a un deportista no lo saca de una serie ya armada: quítalo de la serie en
            "Resultados". Al moverlo aparece en "Inscritos sin serie" de la nueva prueba.
          </p>
        </Card>
      ) : null}
      {dialog}
    </div>
  );
};

export default CategoriasIndividualesArea;
